import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, CheckCircle2, ShieldCheck, FileText, Wrench, Camera, Banknote, MapPin } from "lucide-react";
import { SEO } from "../components/SEO";
import { CTAPair } from "../components/CTA";
import Marquee from "../components/Marquee";
import { MaskedLine, Reveal } from "../components/Motion";
import { TOP_SERVICES, SERVICES } from "../data/services";
import { CITIES, HOMEPAGE_CITIES, getCity } from "../data/cities";
import { BUSINESS } from "../data/site";

const TRUST_ITEMS = ["Licensed & insured", "Mobile rig — we come to you", "Same-day repairs available", "Steel · Aluminum · Stainless"];

const GUARANTEES = [
  { icon: FileText, title: "Written price before work starts", text: "Flat-rate quotes, no hourly surprises." },
  { icon: ShieldCheck, title: "Workmanship warranty", text: "Every repair is backed — if our weld fails, we make it right." },
  { icon: Wrench, title: "Honest repair-or-replace call", text: "If it can't be fixed, no repair charge — only the trip fee applies." },
];

const STEPS = [
  { icon: Camera, num: "01", title: "Send a photo or call", text: "Text a wide shot and a close-up of the damage. That's usually all we need to quote it." },
  { icon: Banknote, num: "02", title: "Get a straight price first", text: "You approve a written, flat price before anything starts. Most quotes answered within the hour during business hours." },
  { icon: Wrench, num: "03", title: "We repair it on-site", text: "Our rig comes to your driveway, job site, or pasture. Most repairs done in a single visit." },
];

const Home = () => {
  const heroRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 70]);

  return (
    <main data-testid="home-page">
      <SEO
        title="Mobile Welding Repair in Fort Worth, TX | We Come To You — Aledo Welding"
        description="Broken gate, cracked trailer, down equipment? Mobile welding repair on-site across Fort Worth, Tarrant & Parker County. Steel, aluminum, stainless. Text a photo for a fast quote."
      />

      {/* HERO */}
      <section ref={heroRef} data-testid="hero" className="bg-paper grain relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-16 md:pt-20 md:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-steel font-semibold mb-5" data-testid="hero-kicker">
              Mobile welding repair · Est. {BUSINESS.established} · Fort Worth, TX
            </p>
            <h1 className="font-display font-black tracking-tight leading-[1.02] text-[2.6rem] sm:text-6xl lg:text-7xl" data-testid="hero-headline">
              <MaskedLine delay={0.05}>Mobile welding repair.</MaskedLine>
              <MaskedLine delay={0.18}>Fast, on-site,</MaskedLine>
              <MaskedLine delay={0.31} className="text-rust">done right.</MaskedLine>
            </h1>
            <Reveal delay={0.5}>
              <p className="mt-6 max-w-xl text-lg text-smoke leading-relaxed" data-testid="hero-subhead">
                On-site welding repair for steel, aluminum, and stainless — at your driveway, job site, or pasture. Most quotes answered within the hour during business hours.
              </p>
              <div className="mt-8">
                <CTAPair testidPrefix="hero-cta" />
              </div>
              <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold text-ink" data-testid="hero-trust-strip">
                {TRUST_ITEMS.map((item, i) => (
                  <li key={item} className="flex items-center gap-2">
                    {i > 0 && <span className="w-1.5 h-1.5 bg-rust rotate-45 hidden sm:inline-block" aria-hidden />}
                    <CheckCircle2 size={16} className="text-steel sm:hidden" aria-hidden />
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <motion.div style={{ y: imgY }} className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-rust pointer-events-none" aria-hidden />
              <img
                src="/images/hero-welder.webp"
                alt="Welder stick-welding a yellow steel rail during an on-site repair"
                data-testid="hero-image"
                className="relative w-full aspect-[4/5] object-cover"
                loading="eager"
              />
              <div className="absolute bottom-0 left-0 bg-coal text-cream px-5 py-3.5 max-w-[80%]">
                <p className="font-display font-bold text-sm uppercase tracking-wider">Repair-first. Always.</p>
                <p className="text-cream/70 text-sm">If it can be fixed, we fix it — on-site.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* SERVICES */}
      <section data-testid="services-section" className="bg-paper py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
              <div className="max-w-2xl">
                <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl" data-testid="services-heading">
                  The repairs we're called for most
                </h2>
                <p className="mt-4 text-smoke leading-relaxed" data-testid="services-audience-note">
                  We work with homeowners who just need their gate to close again — and with contractors, property managers, and equipment operators who need a qualified welder they can dispatch. Either way: straight talk, written prices, on-site repair.
                </p>
              </div>
              <Link to="/services" data-testid="services-see-all" className="group inline-flex items-center gap-2 font-display font-bold text-rust hover:text-rust-dark transition-colors">
                See all {SERVICES.length} services
                <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" aria-hidden />
              </Link>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-px bg-warmline border border-warmline" data-testid="services-grid">
            {TOP_SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.08}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group block bg-cream hover:bg-sand transition-colors duration-200 p-7 sm:p-9 h-full"
                >
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-steel font-semibold mb-3">{s.audience}</p>
                  <h3 className="font-display font-extrabold text-2xl tracking-tight group-hover:text-rust transition-colors">{s.name}</h3>
                  <p className="mt-3 text-smoke leading-relaxed">{s.blurb}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-rust">
                    Repair details <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" aria-hidden />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works" className="bg-cream border-y border-warmline py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal>
            <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl" data-testid="how-heading">
              Three steps. No shop drop-offs. No surprises.
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-px bg-warmline border border-warmline" data-testid="how-steps">
            {STEPS.map((step, i) => (
              <Reveal key={step.num} delay={i * 0.1} className="bg-cream">
                <div className="p-7 sm:p-9 h-full" data-testid={`how-step-${step.num}`}>
                  <div className="flex items-center justify-between mb-6">
                    <step.icon size={30} strokeWidth={1.8} className="text-rust" aria-hidden />
                    <span className="font-display font-black text-5xl text-ink/10">{step.num}</span>
                  </div>
                  <h3 className="font-display font-extrabold text-xl tracking-tight">{step.title}</h3>
                  <p className="mt-3 text-smoke leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GUARANTEE STRIP */}
      <section data-testid="guarantee-strip" className="cut-both bg-charcoal text-cream py-20 md:py-24 -my-[1px] relative z-[5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-3 gap-10 md:gap-8">
          {GUARANTEES.map((g, i) => (
            <Reveal key={g.title} delay={i * 0.08}>
              <div className="flex gap-4" data-testid={`guarantee-${i + 1}`}>
                <g.icon size={28} strokeWidth={1.9} className="text-gold shrink-0 mt-1" aria-hidden />
                <div>
                  <h3 className="font-display font-bold text-lg tracking-tight">{g.title}</h3>
                  <p className="mt-1.5 text-cream/70 text-[15px] leading-relaxed">{g.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* AREAS + REVIEWS */}
      <section data-testid="areas-section" className="bg-cream border-t border-warmline py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <div>
              <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl" data-testid="areas-heading">Where we roll</h2>
              <p className="mt-4 text-smoke leading-relaxed" data-testid="areas-copy">
                Tarrant County is our home ground — most calls answered inside the hour. Parker County to the west is core territory too: ranch gates, cattle guards, and ag equipment on acreage other shops won't drive to.
              </p>
              <div className="mt-7 flex flex-wrap gap-2.5" data-testid="areas-pills">
                {HOMEPAGE_CITIES.map((slug) => {
                  const c = getCity(slug);
                  return (
                    <Link key={slug} to={`/service-areas/${slug}`} data-testid={`area-pill-${slug}`}
                      className="btn-cut inline-flex items-center gap-1.5 border-2 border-ink/70 px-4 py-2 text-sm font-bold hover:bg-charcoal hover:text-cream hover:border-charcoal transition-colors duration-200">
                      <MapPin size={14} aria-hidden /> {c.name}
                    </Link>
                  );
                })}
                <Link to="/service-areas" data-testid="areas-see-all"
                  className="btn-cut inline-flex items-center gap-1.5 bg-rust text-cream px-4 py-2 text-sm font-bold hover:bg-rust-dark transition-colors duration-200">
                  See all {CITIES.length} areas <ArrowRight size={14} aria-hidden />
                </Link>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="reviews-section">
              <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl" data-testid="reviews-heading">Straight from customers</h2>
              <p className="mt-4 text-smoke leading-relaxed">
                We're collecting verified Google & Facebook reviews now. This space is reserved for the real thing — no invented testimonials.
              </p>
              <div className="mt-7 grid gap-4" data-testid="reviews-placeholders">
                {[1, 2, 3].map((n) => (
                  <div key={n} data-testid={`review-slot-${n}`} className="border-2 border-dashed border-ink/25 p-5 text-smoke text-[15px] flex items-center gap-3">
                    <span className="font-display font-black text-2xl text-ink/15">0{n}</span>
                    Your review could be first here — text a photo, get it fixed, tell the neighbors.
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="final-cta" className="cut-top bg-coal text-cream py-20 md:py-28 mt-[-26px] relative z-[5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-left grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-gold font-semibold mb-4">Gate stuck open right now? 24/7 emergency line answered.</p>
            <h2 className="font-display font-black tracking-tight text-3xl sm:text-5xl leading-[1.05]" data-testid="final-cta-heading">
              Two photos. One straight price. Fixed where it sits.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <CTAPair dark testidPrefix="final-cta" />
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
